import { Box, Card, Flex, Heading, Tag, TagLabel, Text } from '@chakra-ui/react';

import { clientHttpTrackEvent } from '~/features/analytics/client-http-track-event';
import { trackEvent } from '~/features/analytics/track-event';
import {
  DownloadSyllabusButton,
  StaticDownloadSyllabusButtonProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/download-syllabus-button';

import { CARD_BORDER_COLOR } from '../constants';
import type { Project } from '../models/projects';

export type ProjectsSubsectionProps = NonNullable<StaticProjectsSubsectionProps> & {
  staticDownloadSyllabusButtonProps?: StaticDownloadSyllabusButtonProps;
};

function ProjectCard({ project }: { project: Project }) {
  return (
    <Card
      border='1px solid'
      borderColor={CARD_BORDER_COLOR}
      borderRadius='8px'
      boxShadow='none'
      flexDirection='column'
      gap={4}
      overflow='hidden'
      padding={4}
      width='100%'
    >
      <Flex flexDirection='column' gap={2} width='100%'>
        {/* Figma fills with #FE914F, which only reaches 2.2:1 against white text. Borrows the
            orange tag's colors, but keeps this badge's flat 4px shape. */}
        <Tag alignSelf='flex-start' borderRadius='4px' boxShadow='none' colorScheme='orange'>
          <TagLabel>{project.projectNumber}</TagLabel>
        </Tag>
        <Flex alignItems='flex-start' flexDirection='column' width='100%'>
          <Heading as='h3' color='supportGray.100' fontFamily='body' fontSize='sm' fontWeight='600' lineHeight='md'>
            {project.name}
          </Heading>
          <Text fontSize='sm' lineHeight='md'>
            {project.description}
          </Text>
        </Flex>
      </Flex>
      {project.pills.length > 0 && (
        <Flex flexWrap='wrap' gap={2} width='100%'>
          {project.pills.map((pill) => (
            <Box
              key={pill}
              alignItems='center'
              bg='white'
              border='1px solid'
              borderColor={CARD_BORDER_COLOR}
              borderRadius='4px'
              // Figma uses rgba(117,117,117,0.75), which is only 2.9:1 on white and fails WCAG AA.
              color='supportGray.60'
              display='flex'
              fontSize='xs'
              fontWeight='600'
              lineHeight='sm'
              px={2}
              py={1}
              whiteSpace='nowrap'
            >
              {pill}
            </Box>
          ))}
        </Flex>
      )}
    </Card>
  );
}

export function ProjectsSubsection({
  programKey,
  projects,
  staticDownloadSyllabusButtonProps,
}: ProjectsSubsectionProps) {
  if (projects.length === 0) return null;

  return (
    <Flex as='section' flexDirection='column' gap={4} id='projects'>
      <Flex alignItems='center' flexWrap='wrap' gap={2} justifyContent='space-between'>
        <Flex flexDirection='column' justifyContent='center'>
          <Heading
            as='h2'
            size={{
              base: 'heading-6',
              md: 'heading-5',
            }}
          >
            What you&apos;ll build
          </Heading>
          <Text fontSize='sm' lineHeight='md'>
            Project submissions reviewed by industry professionals
          </Text>
        </Flex>
        {staticDownloadSyllabusButtonProps && (
          <DownloadSyllabusButton
            {...staticDownloadSyllabusButtonProps}
            isInline
            onAuthClick={(user) =>
              clientHttpTrackEvent({
                type: 'submit',
                name: 'Nanodegree Interested',
                label: `Nanodegree Interested - ${programKey} - STANDARD`,
                additionalAttributes: {
                  product_ids: [programKey],
                  firstName: user?.firstName,
                  phone_number: user?.phoneNumber,
                  last_name: user?.lastName,
                  interested_in: 'syllabus',
                },
              })
            }
            onClick={() =>
              trackEvent({
                type: 'click',
                name: 'Product Page CTA Clicked',
                label: 'syllabus_cta_clicked',
                additionalAttributes: {
                  product_ids: [programKey],
                },
              })
            }
            onFormSubmit={(formData) =>
              clientHttpTrackEvent({
                type: 'submit',
                name: 'Nanodegree Interested',
                label: `Nanodegree Interested - ${programKey} - STANDARD`,
                additionalAttributes: {
                  product_ids: [programKey],
                  firstName: formData.firstName,
                  phone_number: formData.phone,
                  last_name: formData.lastName,
                  interested_in: 'syllabus',
                },
              })
            }
          />
        )}
      </Flex>
      <Flex flexWrap='wrap' gap={4}>
        {projects.map((project) => (
          <Flex
            key={`${project.projectNumber}-${project.name}`}
            flex={{
              base: '0 0 100%',
              md: '0 0 calc(50% - 8px)',
            }}
          >
            <ProjectCard project={project} />
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

/**
 * Resolves the projects for a program at build time. The lookup module is
 * imported dynamically so `projects.json` — which holds the projects for all
 * Project-First PLP programs — stays out of the client bundle and only the
 * current program's projects are serialized into the page props.
 */
export async function transformStaticProjectsSubsectionProps({ programKey }: { programKey: string }) {
  const { getProjects } = await import('../utils/get-projects');
  const projects = getProjects(programKey);

  if (!projects || projects.length === 0) {
    return null;
  }

  return {
    programKey,
    projects,
  };
}

export type StaticProjectsSubsectionProps = Awaited<ReturnType<typeof transformStaticProjectsSubsectionProps>>;
